import { useNavigate } from "react-router-dom";
import { useTheme } from "@mui/material";
import MenuIcon from "@mui/icons-material/Menu";
import SearchIcon from "@mui/icons-material/Search";
import LightModeOutlinedIcon from "@mui/icons-material/LightModeOutlined";
import DarkModeOutlinedIcon from "@mui/icons-material/DarkModeOutlined";
import { useThemeMode } from "ThemeModeProvider";
import DSAppBar from "views/system-design/DSAppBar";
import DSBox from "views/system-design/DSBox";
import DSIconButton from "views/system-design/DSIconButton";
import DSTypography from "views/system-design/DSTypography";
import Clock from "../Widgets/Clock";
import logo from "assets/img/Klydoclock_logo.svg";
import { APPBAR_HEIGHT } from "consts/layout";
import useDeviceType from "components/hooks/useDeviceType";

type AdminNavbarProps = {
  onMenuClick: () => void;
  onSearchClick: () => void;
};

const AdminNavbar = ({ onMenuClick, onSearchClick }: AdminNavbarProps) => {
  const { spacing, palette, zIndex } = useTheme();
  const { isSmallLaptop, isTablet } = useDeviceType();
  const { mode, toggleMode } = useThemeMode();
  const navigate = useNavigate();

  return (
    <DSAppBar
      position="fixed"
      elevation={0}
      sx={{
        height: spacing(APPBAR_HEIGHT),
        backgroundColor: palette.surface.header,
        backdropFilter: "blur(12px)",
        backgroundImage: "none",
        justifyContent: "center",
        px: { xxs: 2, md: 3 },
        zIndex: zIndex.drawer + 1,
      }}
    >
      <DSBox
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 2,
        }}
      >
        <DSBox sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          {isSmallLaptop && (
            <DSIconButton
              onClick={onMenuClick}
              sx={{ color: palette.text.primary }}
              aria-label="open navigation"
            >
              <MenuIcon />
            </DSIconButton>
          )}
          <DSBox
            onClick={() => navigate("/admin/dashboard")}
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1.5,
              cursor: "pointer",
            }}
          >
            <img
              src={logo}
              alt="Klydo"
              width={isTablet ? 90 : 120}
              height="auto"
            />
            <DSTypography
              variant="subtitle1"
              sx={{
                color: palette.surface.accentText,
                fontSize: "14px",
                letterSpacing: "0.12em",
                textTransform: "uppercase",
                display: { xxs: "none", sm: "block" },
              }}
            >
              Backoffice
            </DSTypography>
          </DSBox>
        </DSBox>

        <DSBox
          sx={{ display: "flex", alignItems: "center", gap: 2, minWidth: 0 }}
        >
          {/* command palette trigger */}
          <DSBox
            onClick={onSearchClick}
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              px: { xxs: 1.25, sm: 2 },
              py: 1,
              borderRadius: "999px",
              cursor: "pointer",
              backgroundColor: palette.white[5],
              border: `1px solid ${palette.white[8]}`,
              color: palette.text.disabled,
              transition: "background-color 0.15s ease",
              "&:hover": { backgroundColor: palette.white[8] },
            }}
          >
            <SearchIcon fontSize="small" />
            <DSTypography
              variant="body2"
              sx={{
                color: "inherit",
                display: { xxs: "none", sm: "block" },
                pr: 2,
              }}
            >
              Jump to…
            </DSTypography>
            <DSBox
              sx={{
                px: 0.75,
                py: 0.1,
                borderRadius: "6px",
                border: `1px solid ${palette.white[12]}`,
                fontSize: "11px",
                fontFamily: "Gilroy-SemiBold",
                display: { xxs: "none", sm: "block" },
              }}
            >
              ⌘K
            </DSBox>
          </DSBox>
          <DSIconButton
            onClick={toggleMode}
            aria-label="toggle theme"
            sx={{ color: palette.text.secondary }}
          >
            {mode === "dark" ? (
              <LightModeOutlinedIcon fontSize="small" />
            ) : (
              <DarkModeOutlinedIcon fontSize="small" />
            )}
          </DSIconButton>
          <DSBox sx={{ display: { xxs: "none", md: "flex" } }}>
            <Clock tz="America/Los_Angeles" />
          </DSBox>
        </DSBox>
      </DSBox>
    </DSAppBar>
  );
};

export default AdminNavbar;
